import React, { FC, useContext, useEffect, useState } from "react";
import { Dialog } from "@natera/material/lib/dialog";
import Button from "@natera/material/lib/button";
import { defineMessages, useIntl } from "react-intl";
import { TestDetailsContext, UserContext } from "@app/provider";
import { SessionContext } from "@app/pippa/session";
import {
  DialogsContext,
  PippaContext,
  APIContext,
  EventsContext,
} from "@app/pippa/provider";
import { unescape } from "@app/pippa/helpers";
import "./acknowledgementDialog.scss";
import { CreateNewUserProps } from "@app/pippa/hooks/useApiController";
import { usePippaAnalytics } from "@app/pippa/hooks/usePippaAnalytics";
import { v4 as uuidv4 } from "uuid";
import { MessageTypes, EventDataMessageType } from "@app/pippa/models";
import { buildActionMessages } from "@app/pippa/helpers/buildActionMessages";

interface Props {
  onClose: () => void;
}

const messages = defineMessages({
  agreeButtonTitle: {
    id: "agreeButtonTitle",
    defaultMessage: "Agree and start a chat",
  },
  acknowledgementText: {
    id: "acknowledgementText",
    defaultMessage:
      'By clicking \'Agree and start a chat\', you consent to Natera collecting and using the contents of this chat in accordance with our <a class="linkEdited" tabindex="0" rel="noopener noreferrer" target="_blank" href="https://www.natera.com/privacy/" contenteditable="false" id="1657891670648" data-extension="false" activity-event="ext_website" aria-label="privacy policy"><span class="linkCreated">Privacy Policy<svg role="img" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.1442 7.80378L6.81147 17.1269C6.673 17.2653 6.49897 17.333 6.28937 17.3298C6.07975 17.3266 5.90572 17.2557 5.76727 17.1173C5.6288 16.9788 5.55957 16.8064 5.55957 16.6C5.55957 16.3936 5.6288 16.2211 5.76727 16.0827L15.0904 6.74995H6.89417C6.68167 6.74995 6.50355 6.67805 6.3598 6.53425C6.21606 6.39043 6.1442 6.21223 6.1442 5.99965C6.1442 5.78705 6.21606 5.60896 6.3598 5.46538C6.50355 5.32179 6.68167 5.25 6.89417 5.25H16.7403C16.9964 5.25 17.2111 5.33662 17.3843 5.50985C17.5575 5.68308 17.6441 5.89774 17.6441 6.15383V16C17.6441 16.2125 17.5722 16.3906 17.4284 16.5343C17.2846 16.6781 17.1064 16.75 16.8938 16.75C16.6812 16.75 16.5032 16.6781 16.3596 16.5343C16.216 16.3906 16.1442 16.2125 16.1442 16V7.80378Z" fill="#0377C4" fill-opacity="0.87" ></path></svg></span></a>. You understand that you are interacting with an <b>automated artificial intelligence system.</b><br><br>This system is strictly for general administrative info retrieval and scheduling. It cannot provide medical advice, evaluate symptoms, or assist with clinical care. If you require medical assistance, this chat will close and redirect you to your provider.',
  },
  chatMessageDialogExternalLink: {
    id: "chatMessageDialogExternalLink",
    defaultMessage: "Open the external resource link",
  },
  assistantUnavailable: {
    id: "pippaAssistantUnavailable",
    defaultMessage:
      "Our assistant is temporarily unavailable. Please try again later.",
  },
  testResultsServiceUnavailable: {
    id: "testResultsServiceUnavailable",
    defaultMessage:
      "Our test results service is temporarily unavailable, so we're unable to provide personalized information at this time. Please try again later.",
  },
});

const AcknowledgementDialog: FC<Props> = ({ onClose }) => {
  const intl = useIntl();
  const { getTestDetails } = useContext(TestDetailsContext);
  const { uppUser } = useContext(UserContext);
  const { acknowledgementDialog } = useContext(DialogsContext);
  const { setIsWidgetOpen, ingressSource } = useContext(PippaContext);
  const { updateSessionActivity } = useContext(SessionContext);
  const { addMessages, setShowTyping, setIsInputHidden } =
    useContext(EventsContext);
  const { apiProviderState, getUserChatId, getWelcomeMessages } =
    useContext(APIContext);
  const { trackConsentYes, trackConsentClosed, trackUserPlatform } =
    usePippaAnalytics();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchWelcomeMessages = async () => {
      if (apiProviderState.chatId) {
        try {
          setShowTyping(true);
          const response = await getWelcomeMessages(apiProviderState.chatId);
          updateSessionActivity(apiProviderState.chatId);

          const transformedMessages = buildActionMessages(response.messages);

          addMessages(transformedMessages);
          setShowTyping(false);

          acknowledgementDialog.close();
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching welcome messages:", error);
          setShowTyping(false);
          showUnavailableMessage(
            intl.formatMessage(messages.assistantUnavailable)
          );
        }
      }
    };

    fetchWelcomeMessages();
  }, [apiProviderState.chatId]);

  const showUnavailableMessage = (text: string) => {
    const errorMessage: EventDataMessageType = {
      id: uuidv4(),
      type: MessageTypes.TEXT,
      text,
      isSendByUser: false,
      isError: true,
    };

    setIsInputHidden(true);
    addMessages([errorMessage]);
    setIsLoading(false);
    acknowledgementDialog.close();
  };

  const handleAgreeButton = async () => {
    setIsLoading(true);
    trackConsentYes();
    trackUserPlatform();
    const testDetails = getTestDetails();
    const patientUid = uppUser?.patientUID;

    if (!testDetails || !testDetails.resultCode) {
      showUnavailableMessage(
        intl.formatMessage(messages.testResultsServiceUnavailable)
      );
      return;
    }

    const requestBody = {
      patientUid,
      limsCaseId: testDetails.limsId?.toString(),
      testType: testDetails.testType,
      firstName: testDetails.patient?.firstName,
      testResults: {
        resultCode: testDetails.resultCode,
        historicalMtmValues: testDetails.historicalMtmValues,
        currentMtmValue: testDetails.currentMtmValue,
        mtmDelta: testDetails.mtmDelta,
      },
    } as CreateNewUserProps;

    try {
      await getUserChatId(requestBody);
    } catch (error) {
      console.error("Error creating pippa user:", error);
      showUnavailableMessage(intl.formatMessage(messages.assistantUnavailable));
    }
  };

  const onCloseHandler = () => {
    trackConsentClosed(ingressSource);
    onClose && onClose();
    setIsWidgetOpen(false);
  };

  const actions = (
    <Button
      className="acknowledgement-dialog-button"
      raised
      onClick={handleAgreeButton}
      loading={isLoading}
    >
      {intl.formatMessage(messages.agreeButtonTitle)}
    </Button>
  );

  return (
    <Dialog
      className="acknowledgement-dialog"
      onClose={onCloseHandler}
      actions={actions}
      focusTrapOptions={{ skipInitialFocus: true }}
      showCrossButton
    >
      <div
        className="dialog-container"
        dangerouslySetInnerHTML={{
          __html: unescape(intl.formatMessage(messages.acknowledgementText)),
        }}
      />
    </Dialog>
  );
};

export default AcknowledgementDialog;
